import { useEffect, useState } from 'react';

// eslint-disable-next-line no-restricted-syntax, @typescript-eslint/naming-convention
import type * as _braintree from 'braintree-web';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { getWindow } from '@change-corgi/core/window';

import { fetchBraintreeClientData } from 'src/app/shared/api/payments';
import { loadPaypalScripts } from 'src/app/shared/utils/payments';

import type { PaypalState } from './context';

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Window {
		// Use the imported typedefs for the global braintree object that was created by the braintree sdk that was loaded with a script tag
		braintree: typeof _braintree;
	}
}

export function usePaypalState(): PaypalState {
	const utilityContext = useUtilityContext();
	const [state, setState] = useState<PaypalState>({ status: 'loading' });

	useEffect(() => {
		(async function initializePaypal() {
			try {
				const [{ clientToken }] = await Promise.all([
					fetchBraintreeClientData(utilityContext),
					loadPaypalScripts(utilityContext.errorReporter.report),
				]);

				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
				if (!getWindow().braintree?.client) {
					setState({ status: 'error' });
					return;
				}

				const braintreeClientInstance = await getWindow().braintree.client.create({ authorization: clientToken });
				let dataCollectorInstance: _braintree.DataCollector | undefined;
				try {
					dataCollectorInstance = await getWindow().braintree.dataCollector.create({
						client: braintreeClientInstance,
						paypal: true,
					});
				} catch (error) {
					void utilityContext.errorReporter.report(
						{
							error: new Error('Data Collector Instance Create Error', { cause: error }),
						},
						{
							severity: 'warning',
						},
					);
				}

				const paypalCheckoutInstance = await getWindow().braintree.paypalCheckout.create({
					client: braintreeClientInstance,
				});

				// Note: PayPal SDK loading is now handled by PayPalScriptProvider
				// from @paypal/react-paypal-js using BraintreePayPalButtons
				setState({
					status: 'loaded',
					paypalCheckout: paypalCheckoutInstance,
					braintreeClientInstance,
					deviceData: dataCollectorInstance?.deviceData,
					clientToken,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
				void utilityContext.errorReporter.report(e);
				setState({ status: 'error' });
			}
		})();
	}, [utilityContext]);

	return state;
}
